/**
 * A field device's error, as an operator reads it.
 *
 * The gateway's drivers report errors in their libraries' words ("enip: connect
 * 192.168.1.16:44818: cannot connect to controller: dial tcp …: i/o timeout", "modbus:
 * exception '2' (illegal data address)", "CPU : Function refused by CPU",
 * "StatusBadSecurityPolicyRejected", "melsec: end code 0xc059"), and the platform relays
 * them as they are (a 424 `scan_failed` detail). This turns the ones it recognises into a
 * title (what happened), a hint (what to check, in the protocol's own terms) and keeps the
 * raw text for Details. The Android app has the same rules
 * (apps/mobile/android-customer/…/features/gateway/DeviceErrors.kt); keep them in step.
 */

export type DeviceProblemKind =
  | "no_route"
  | "timeout"
  | "refused"
  | "closed"
  | "name"
  | "address"
  | "access"
  | "setup"
  | "device"
  | "unknown";

export interface DeviceProblem {
  kind: DeviceProblemKind;
  title: string;
  hint?: string;
  /** The gateway's own text, shown under Details. */
  raw: string;
  /** The driver the error came from, when known (`enip`, `modbus-tcp`, …). */
  driver?: string;
}

/**
 * The lead-ins that put a device's own error after them: the platform's DEVICE_FAILED
 * copy and scanErrorMessage's (lib/gateway-client.ts). The text after the first ": " that
 * follows one of these is the gateway's.
 */
const LEADS = [/couldn't reach the device/i, /did not answer/i, /could not finish the scan/i];

const HOST_PORT = /(\d{1,3}(?:\.\d{1,3}){3}):(\d{1,5})/;
const HOST = /\b(\d{1,3}(?:\.\d{1,3}){3})\b/;

/** The gateway's own text in a message that wraps it, else the message. */
export function deviceErrorRaw(message: string): string {
  const m = message.trim();
  for (const lead of LEADS) {
    const at = m.search(lead);
    if (at < 0) continue;
    const colon = m.indexOf(": ", at);
    if (colon >= 0) return m.slice(colon + 2).trim();
  }
  return m;
}

/**
 * The problem `message` describes, or null when it isn't a device error this knows (a
 * platform or browser message, already written for a person): show it as it is.
 */
export function explainDeviceError(message: string, driver?: string): DeviceProblem | null {
  const raw = deviceErrorRaw(message);
  if (!raw) return null;
  const low = raw.toLowerCase();
  const d = driver || driverOf(low);
  const hp = HOST_PORT.exec(raw);
  const host = hp?.[1] ?? HOST.exec(raw)?.[1];
  const port = hp?.[2];
  const where = host ? (port ? `${host}:${port}` : host) : "the device";
  const p = (kind: DeviceProblemKind, title: string, hint?: string): DeviceProblem => ({ kind, title, hint, raw, driver: d });

  const specific = protocolSpecific(low, d);
  if (specific) return p(specific.kind, specific.title, specific.hint);

  if (/no route to host|network is unreachable|host is unreachable|host is down/.test(low)) {
    return p(
      "no_route",
      `Can't reach ${host ?? "the device"}`,
      "The gateway has no way to that address. Check the device is on, and that one of the gateway's adapters is on its network.",
    );
  }
  if (low.includes("connection refused")) {
    return p(
      "refused",
      host ? `${host} refused the connection${port ? ` on port ${port}` : ""}` : "The device refused the connection",
      `The device is there, but nothing answers on that port. ${portAdvice(d)}`,
    );
  }
  if (/i\/o timeout|timed out|deadline exceeded|timeout/.test(low)) {
    return p(
      "timeout",
      `No answer from ${where}`,
      "The device didn't answer in time. Check the address and port, that the device is on, and that it is on the same network as one of the gateway's adapters.",
    );
  }
  if (/connection reset|broken pipe|closed by|use of closed network connection/.test(low) || low === "eof" || low.endsWith(": eof")) {
    return p(
      "closed",
      "The device closed the connection",
      "It may allow only a few connections at once (an HMI or SCADA can hold them all), or it restarted. Try again in a moment.",
    );
  }
  if (low.includes("no such host") || low.includes("lookup ")) {
    return p("name", "Can't find that device name", "Use the device's IP address.");
  }
  return d ? p("unknown", "The device returned an error") : null;
}

function driverOf(low: string): string | undefined {
  if (low.startsWith("enip") || low.includes("forward open")) return "enip";
  if (low.startsWith("modbus")) return "modbus-tcp";
  if (low.startsWith("opcua") || low.includes("statusbad")) return "opcua";
  if (low.startsWith("s7") || low.startsWith("cpu :") || low.startsWith("cli :") || low.startsWith("iso :")) return "s7";
  if (low.startsWith("melsec")) return "melsec";
  return undefined;
}

function portAdvice(driver?: string): string {
  switch (driver) {
    case "enip":
      return "EtherNet/IP uses port 44818.";
    case "modbus-tcp":
    case "modbus-rtu":
      return "Modbus TCP usually uses port 502; some devices use another. Check the device's setting.";
    case "s7":
      return "Siemens S7 uses port 102.";
    case "opcua":
      return "OPC UA servers usually use port 4840; check the server's endpoint URL.";
    case "melsec":
      return "MC protocol needs an open port set up on the PLC in GX Works (for example 5000), with the protocol set to MC.";
    default:
      return "Check the port for this protocol.";
  }
}

type Found = { kind: DeviceProblemKind; title: string; hint?: string };

function protocolSpecific(low: string, d?: string): Found | null {
  switch (d) {
    case "modbus-tcp":
    case "modbus-rtu":
      return modbus(low);
    case "enip":
      return enip(low);
    case "s7":
      return s7(low);
    case "opcua":
      return opcua(low);
    case "melsec":
      return melsec(low);
    default:
      return null;
  }
}

// ── Modbus (goburrow: "modbus: exception '2' (illegal data address), function '131'") ──
function modbus(low: string): Found | null {
  const code = Number(/exception '(\d+)'/.exec(low)?.[1] ?? NaN);
  if (code === 1 || low.includes("illegal function")) {
    return {
      kind: "address",
      title: "The device doesn't support this read",
      hint: "Its Modbus function code isn't supported. Try the other table (holding or input registers, coils or discrete inputs), per the device's manual.",
    };
  }
  if (code === 2 || low.includes("illegal data address")) {
    return {
      kind: "address",
      title: "That register isn't on the device",
      hint: "Check the address against the device's register map. Addresses here start at 0: holding register 40001 is 0. Reading a block that runs past the last register fails too.",
    };
  }
  if (code === 3 || low.includes("illegal data value")) {
    return { kind: "address", title: "The device refused the size of the read", hint: "Read fewer registers at a time." };
  }
  if (code === 4 || low.includes("device failure")) return { kind: "device", title: "The device reported an internal failure" };
  if (code === 6 || low.includes("busy")) return { kind: "device", title: "The device is busy", hint: "Try again in a moment." };
  if (code === 10 || code === 11 || low.includes("gateway path") || low.includes("target device failed")) {
    return {
      kind: "setup",
      title: "A Modbus gateway couldn't reach the device behind it",
      hint: "Check the unit ID (slave ID), and the RS-485 wiring and settings behind the Modbus gateway.",
    };
  }
  if (low.includes("short") && low.includes("read")) {
    return { kind: "device", title: "The device sent back less than asked", hint: "Read fewer registers at a time." };
  }
  return null;
}

// ── EtherNet/IP (gologix) ──
function enip(low: string): Found | null {
  if (low.includes("did not return its tag list") || low.includes("browse_unsupported")) {
    return {
      kind: "setup",
      title: "This device can't list its tags",
      hint: "Enter tag names by hand. Program tags are written Program:MainProgram.Tag.",
    };
  }
  if (low.includes("forward open")) {
    return {
      kind: "setup",
      title: "The controller refused the connection",
      hint: "Check the route: a CompactLogix or ControlLogix CPU in slot 0 is backplane 1, slot 0. The controller may also be out of free connections (HMIs and SCADA use them).",
    };
  }
  if (low.includes("path destination unknown") || low.includes("path segment")) {
    return {
      kind: "address",
      title: "Tag or route not found",
      hint: "Check the tag's name and scope (a program tag is Program:Name.Tag) and the controller's slot.",
    };
  }
  if (low.includes("privilege violation")) {
    return {
      kind: "access",
      title: "The controller refused access to that tag",
      hint: "In Studio 5000, set the tag's External Access to Read Only or Read/Write.",
    };
  }
  if (low.includes("register session") || low.includes("listidentity")) {
    return {
      kind: "setup",
      title: "It doesn't answer as an EtherNet/IP device",
      hint: "Check the address and that the device speaks EtherNet/IP (CIP) on port 44818.",
    };
  }
  return null;
}

// ── Siemens S7 (gos7: "CPU : Function refused by CPU", "ISO : Invalid PDU received") ──
function s7(low: string): Found | null {
  if (low.includes("function refused") || low.includes("function not available")) {
    return {
      kind: "access",
      title: "The PLC blocks remote reads (PUT/GET)",
      hint: "In TIA Portal: CPU properties, Protection & Security, Connection mechanisms: allow access with PUT/GET. For a data block, turn off Optimized block access.",
    };
  }
  if (low.includes("address out of range")) {
    return {
      kind: "address",
      title: "That address doesn't exist on the PLC",
      hint: "Check the DB number and byte offset, and that the DB is long enough.",
    };
  }
  if (low.includes("item not available")) {
    return {
      kind: "address",
      title: "That data block isn't there",
      hint: "Check the DB number. A DB with Optimized block access can't be read this way.",
    };
  }
  if (low.startsWith("iso :") || low.includes("invalid pdu") || low.includes("cotp")) {
    return {
      kind: "setup",
      title: "The PLC refused the S7 connection",
      hint: "Check rack and slot: an S7-1200 or S7-1500 is rack 0, slot 1; an S7-300 is rack 0, slot 2.",
    };
  }
  return null;
}

// ── OPC UA (gopcua status codes) ──
function opcua(low: string): Found | null {
  if (/badsecuritypolicyrejected|badsecuritymoderejected|no endpoint with security policy none/.test(low)) {
    return {
      kind: "setup",
      title: "The server wants a secure connection",
      hint: "The gateway connects with security None. Allow the None policy on the OPC UA server.",
    };
  }
  if (low.includes("badcertificate")) {
    return {
      kind: "access",
      title: "The server doesn't trust the gateway",
      hint: "Trust the gateway's certificate on the server (it is usually in the server's rejected certificates).",
    };
  }
  if (low.includes("baduseraccessdenied") || low.includes("badidentitytoken")) {
    return {
      kind: "access",
      title: "User name or password rejected",
      hint: "Check the OPC UA user name and password in the device's connection settings.",
    };
  }
  if (/badnodeidunknown|has no node|has no namespace/.test(low)) {
    return { kind: "address", title: "That node isn't on the server", hint: "Check the node ID, including its namespace (ns=2;s=Tag)." };
  }
  if (low.includes("badtoomanysessions")) {
    return { kind: "device", title: "The server has no free sessions", hint: "Close other clients, or raise the server's session limit." };
  }
  if (low.includes("badnotreadable")) return { kind: "access", title: "That node can't be read" };
  if (low.includes("reported no endpoints")) {
    return { kind: "setup", title: "The server offered no way to connect", hint: "Check the endpoint URL and the server's endpoint settings." };
  }
  return null;
}

// ── MELSEC MC protocol ("melsec: end code 0xc059 (…)") ──
function melsec(low: string): Found | null {
  const code = /end code 0x([0-9a-f]{4})/.exec(low)?.[1];
  if (low.includes("not a 3e binary reply") || code === "c059") {
    return {
      kind: "setup",
      title: "The PLC's port isn't set to MC protocol, 3E binary",
      hint: "In GX Works, set this Ethernet port's protocol to MC protocol, 3E frame, binary code.",
    };
  }
  if (code === "c056" || code === "c058") {
    return { kind: "address", title: "That device address is out of range", hint: "Check the device number against the PLC's device memory." };
  }
  if (code && ["c051", "c052", "c053", "c054"].includes(code)) {
    return { kind: "address", title: "Too many points in one request", hint: "Read fewer points at a time." };
  }
  if (code) {
    return {
      kind: "device",
      title: `The PLC refused the request (end code 0x${code.toUpperCase()})`,
      hint: "Look the end code up in the MELSEC Ethernet module's manual.",
    };
  }
  return null;
}
